import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SummaryBoxChart.css";
// @ts-ignore
import { api } from "../../../api/client.tsx";
// @ts-ignore
import { Loader } from "../../../Loading/Loading.tsx";

type Severity = "Critical" | "High" | "Medium" | "Low";

type PersistObsoleteResp = {
  [K in Severity]?: { Persisting?: number; Obsolete?: number };
};

type L1Row = {
  image?: string;
  Image?: string;
  version?: string;
  total_total?: number;
};

function repoFromImageId(id: string | undefined): string {
  if (!id) return "";
  const s = String(id);
  const m = s.match(/^([^/_-]+)/);
  return m ? m[1] : s.split("_")[0] || s;
}

function fmt(n: number) {
  return n.toLocaleString();
}

function CardTitle({
  children,
  tip,
  id,
}: {
  children: React.ReactNode;
  tip: string;
  id: string;
}) {
  return (
    <div className="sumBox-titleRow">
      <div className="sumBox-title">{children}</div>
      <button
        type="button"
        className="sb-help"
        aria-label={`What is "${children}"?`}
        aria-describedby={id}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        ?
        <span id={id} className="sb-help-tip" role="tooltip">
          {tip}
        </span>
      </button>
    </div>
  );
}

export default function SummaryBoxChart({ selectedImage }: { selectedImage: string }) {
  const navigate = useNavigate();
  const repoKey = useMemo(() => repoFromImageId(selectedImage), [selectedImage]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [persistObsolete, setPersistObsolete] = useState<PersistObsoleteResp>({});
  const [percentBreakdown, setPercentBreakdown] = useState<Record<string, number>>({});
  const [l1Rows, setL1Rows] = useState<L1Row[]>([]);

  useEffect(() => {
    if (!repoKey) return;
    let canceled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [po, pct, l1] = await Promise.all([
          api.stats.persistingVsObsolete(repoKey),
          api.stats.cveBreakdownForRepo(repoKey),
          api.level1.forRepo(repoKey),
        ]);
        if (!canceled) {
          setPersistObsolete(po || {});
          setPercentBreakdown(pct || {});
          setL1Rows(Array.isArray(l1) ? l1 : []);
          setLoading(false);
        }
      } catch {
        if (!canceled) {
          setLoading(false);
          setErr("Failed to load summary.");
          setPersistObsolete({});
          setPercentBreakdown({});
          setL1Rows([]);
        }
      }
    })();
    return () => {
      canceled = true;
    };
  }, [repoKey]);

  const totals = useMemo(() => {
    const sev: Severity[] = ["Critical", "High", "Medium", "Low"];
    let pers = 0;
    let obs = 0;
    for (const s of sev) {
      pers += Number(persistObsolete?.[s]?.Persisting ?? 0);
      obs += Number(persistObsolete?.[s]?.Obsolete ?? 0);
    }
    return { pers, obs, total: pers + obs };
  }, [persistObsolete]);

  const dominantSeverity = useMemo(() => {
    const candidates: Severity[] = ["Critical", "High", "Medium", "Low"];
    if (percentBreakdown && Object.keys(percentBreakdown).length) {
      return candidates.reduce<Severity | null>((best, s) => {
        if (
          best === null ||
          Number(percentBreakdown[s] ?? 0) > Number(percentBreakdown[best] ?? 0)
        ) {
          return s;
        }
        return best;
      }, null);
    }
    let best: Severity | null = null;
    let bestVal = -1;
    for (const s of candidates) {
      const v =
        Number(persistObsolete?.[s]?.Persisting ?? 0) +
        Number(persistObsolete?.[s]?.Obsolete ?? 0);
      if (v > bestVal) {
        bestVal = v;
        best = s;
      }
    }
    return best;
  }, [percentBreakdown, persistObsolete]);

  const topVersion = useMemo(() => {
    if (!l1Rows.length) return { image: "", label: "", count: 0 };
    const withCounts = l1Rows.map((r) => {
      const id = (r.image ?? r.Image ?? "") as string;
      const count = Number(r.total_total ?? 0);
      const label = id.includes("_")
        ? id.split("_").slice(1).join("_")
        : (r.version ?? id);
      return { image: id, label, count };
    });
    withCounts.sort((a, b) => b.count - a.count);
    return withCounts[0] || { image: "", label: "", count: 0 };
  }, [l1Rows]);

  if (!repoKey) return null;

  return (
    <div className="summaryBox-panel-summary" role="region" aria-labelledby="l1-kpi-title">
      <div className="summaryBox-header">
        <h3 id="l1-kpi-title">
          Summary for <code>{repoKey}</code>
        </h3>

        <button
          type="button"
          className="sb-help sb-help-lg"
          aria-label="What is this summary?"
          aria-describedby="sumBox-tip"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          ?
          <span id="sumBox-tip" className="sb-help-tip" role="tooltip">
            Key KPIs for the selected repository: total CVEs, how many are persisting vs.
            obsolete, the most common (dominant) severity across versions, and the version
            with the most total CVEs. Click the version chip to open detailed vulnerabilities
            for that image tag.
          </span>
        </button>
      </div>

      <section className="sumBox-root" aria-label="Repository summary KPIs">
        {loading ? (
          <div className="sumBox-loading">
            <Loader />
          </div>
        ) : err ? (
          <div className="sumBox-error">{err}</div>
        ) : (
          <div className="sumBox-grid">
            <div className="sumBox-card">
              <div className="sumBox-value">{fmt(totals.total)}</div>
              <CardTitle
                id="tip-total-cves"
                tip="Sum of persisting and obsolete CVEs across all versions of this repository."
              >
                Total CVEs
              </CardTitle>
            </div>

            <div className="sumBox-card">
              <div className="sumBox-value">{fmt(totals.pers)}</div>
              <CardTitle
                id="tip-persisting"
                tip="CVEs still present in the latest version lineage (not fixed or removed)."
              >
                Persisting
              </CardTitle>
            </div>

            <div className="sumBox-card">
              <div className="sumBox-value">{fmt(totals.obs)}</div>
              <CardTitle
                id="tip-obsolete"
                tip="CVEs that existed in older versions but are not present in the latest lineage (fixed or removed)."
              >
                Obsolete
              </CardTitle>
            </div>

            <div className="sumBox-card">
              <CardTitle
                id="tip-dominant"
                tip="The severity bucket with the largest share of CVEs for this repository."
              >
                Dominant Severity
              </CardTitle>
              <div className={`sumBox-badge sev-${(dominantSeverity || "na").toLowerCase()}`}>
                {dominantSeverity ?? "—"}
              </div>
            </div>

            <div className="sumBox-card">
              <CardTitle
                id="tip-top-version"
                tip="The version (image tag) with the highest total CVE count. Click to view details."
              >
                Top Version
              </CardTitle>
              {topVersion.image ? (
                <button
                  className="sumBox-chip"
                  onClick={() =>
                    navigate(`/vulnerabilities/${encodeURIComponent(topVersion.image)}`)
                  }
                  title="Open details"
                >
                  <span className="sumBox-chipLabel">{topVersion.label}</span>
                  <span className="sumBox-chipCount">{fmt(topVersion.count)}</span>
                </button>
              ) : (
                <div className="sumBox-empty">—</div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
